import React, { useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import get from 'lodash.get';
import has from 'lodash.has';

import { Div, Col, Row, Text, Table } from '@pinpoint/design-kit';
import { Button } from 'src/components';
import CONSTANTS from 'src/store/constants';

/*
 * There is a weird behavior where if we exceed 998 rows
 * the table rows starts to overlap on one another. Hence
 * we try to max the number of rows to 998.
 * */
const MAX_ROWS = 998;

// eslint-disable-next-line react/prop-types
function Validation(entry) {
  const target = get(entry, 'target_variable', get(entry, 'validation'));
  if ([null, ''].includes(target)) return null;
  return (
    <Div
      tag="span"
      w="1rem"
      h="1rem"
      bg={target ? 'tableauOrange' : 'tableauBlue'}
      rounded="circle"
      data-tip={target ? 'HIT' : 'MISS'}
    />
  );
}

export default function ClassifierMode({
  modelView: { model, downloadUrl, fetchDownloadUrlStatus, fetchDownloadUrlError },
  fetchDownloadUrl,
  enqueueNotification,
}) {
  const talScoresSampled = get(
    model,
    'modelData.dataPayload.tal_scores_sampled',
    get(model, 'modelData.dataPayload.talScoresSampled', [])
  );
  const hasValidation = useMemo(
    () => has(talScoresSampled, '[0].target_variable') || has(talScoresSampled, '[0].validation'),
    [talScoresSampled]
  );
  const hasMatchType = useMemo(
    () => has(talScoresSampled, '[0].match_type') || has(talScoresSampled, '[0].matchType'),
    [talScoresSampled]
  );
  const handleFetchDownloadUrl = useCallback(() => fetchDownloadUrl(model.id), [fetchDownloadUrl, model.id]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  }, [fetchDownloadUrlStatus]);

  useEffect(() => {
    if (fetchDownloadUrlStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Download failed. Reason: ${fetchDownloadUrlError}`);
    }
    if (fetchDownloadUrlStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Download sucessfully completed', { autoClose: true });
      // Adding a setTimeout to prevent multiple downloads
      // The `fetchDownloadUrlStatus` statys for 500 milliseconds before it resets.
      // If the download on the new tab completes within 500 milliseconds, the control
      // comes back on to the tab and triggers the useEffect block to download the file
      // again and it continues until 500 milliseconds elapses. Delayig opening a new tab
      // by a second to work around this issue
      setTimeout(() => {
        window.open(downloadUrl, '_blank');
      }, 1000);
    }
  }, [fetchDownloadUrlStatus, downloadUrl, enqueueNotification, fetchDownloadUrlError]);

  const columns = useMemo(() => {
    const defaultCols = [
      {
        id: 'pin',
        Header: <Text data-test="crm-header">CRM ID</Text>,
        justify: 'left',
        padding: { l: '1rem' },
        cellPadding: { l: '1rem' },
        cellJustify: 'left',
        accessor: (entry) => get(entry, 'pin', get(entry, 'CRM ID', get(entry, 'CRM_ID', 'MISSING CRM ID'))),
        disableSortBy: true,
      },
      {
        id: 'talScore',
        Header: <Text data-test="prediction-header">Prediction Value</Text>,
        cellJustify: 'center',
        accessor: (entry) => {
          const val = get(entry, 'tal_score', get(entry, 'prediction_value'));
          return val ? val.toFixed(2) : '-';
        },
      },
      {
        id: 'percentile',
        Header: <Text data-test="perc-header">Prediction Percentile</Text>,
        cellJustify: 'center',
        accessor: (entry) => {
          const percentile = get(entry, 'percentile', get(entry, 'prediction_percentile'));
          if (percentile) {
            return percentile < 1 ? (percentile * 100).toFixed(1) : percentile.toFixed(1);
          }
          return '-';
        },
      },
    ];
    if (hasMatchType) {
      defaultCols.splice(1, 0, {
        id: 'matchType',
        Header: <Text data-test="match-type-header">Match Type</Text>,
        cellJustify: 'center',
        accessor: (row) => get(row, 'matchType', get(row, 'match_type', '-')),
      });
    }
    if (hasValidation) {
      defaultCols.push({
        id: 'validation',
        Header: <Text data-test="val-hdr">Risk Outcome</Text>,
        cellJustify: 'center',
        accessor: Validation,
        disableSortBy: true,
      });
    }
    return defaultCols;
  }, [hasValidation, hasMatchType]);

  return (
    <Div h="100%" w="100%" maxH="100%" p={{ x: '1.5rem', y: '1rem' }} d="flex" flexDir="column">
      <Row data-test="title-row" flexWrap="nowrap">
        <Col data-test="title-btns-col" gutterWidth="0" d="flex" align="flex-start" justify="flex-start" size="12">
          <Button
            buttonStyle="primary"
            onClick={handleFetchDownloadUrl}
            isLoading={fetchDownloadUrlStatus === CONSTANTS.API.STARTED}
            data-test="dl-btn"
          >
            DOWNLOAD HOLDOUT PREDICTIONS
          </Button>
        </Col>
      </Row>
      <Row>
        <Text m={{ t: '0.25rem', l: '1rem', b: '1.25rem' }}>Below is a sampled-down list:</Text>
      </Row>
      <Row overflow="scroll" flexGrow="1">
        <Col data-test="title-btns-col" gutterWidth="0" size="12">
          <Table
            h="10vh" // the height will still overflow, it just needs to be an absolute value with overflow undefined
            overflow={undefined}
            columns={columns}
            data={talScoresSampled.slice(0, MAX_ROWS)}
            gridState={{ sortBy: [{ id: 'talScore', desc: true }] }}
            m="0"
            p="0"
          />
        </Col>
      </Row>
    </Div>
  );
}

ClassifierMode.propTypes = {
  modelView: PropTypes.shape({
    model: PropTypes.shape({
      id: PropTypes.number,
      dataModelType: PropTypes.string.isRequired,
    }).isRequired,
    downloadUrl: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    fetchDownloadUrlStatus: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
    fetchDownloadUrlError: PropTypes.oneOf([PropTypes.string, undefined]).isRequired,
  }).isRequired,
  fetchDownloadUrl: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};
